import h from '../../../helpers/common';
import f2 from '../../../helpers/formatter2';
import f from '../../../helpers/formatter';
import { Text, FlexWrapper, Button, HeroBlock, MovieDetails, FontIcon, DetailedPoster } from '@intigral/shared';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { Carousel2, MovieButtons } from '@intigral/ui';
import { __ } from '../../../translations';
import withResposiveStyle from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import { useEffect, useState } from 'react';
import { getCatchUpEpisodesApi, getMixedCatchUpEpisodesApi } from '../../../services/serviceWrapper/mpxServiceWrapper';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import moment from 'moment';
import channelsUtil from '../../../helpers/channelsUtil';
import { EpisodeDetailEntry } from '@intigral/domain/lib/entity/interface/iMpxVodResponse';
import CatchUpCard from './CatchUpCard';
import { useNavigate } from 'react-router-dom';
import { ITransformedCatchUp } from '../../../utils/transformResponse/catchupResponse';
import { useSelector } from 'react-redux';
import { checkShowLockIcon } from '../../../helpers/vod';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { toast } from '@intigral/shared';

const MButtons = withResposiveStyle(MovieButtons);
const MBDetails = withResposiveStyle(MovieDetails);

interface ICatchupDetailProps {
    catchupData?: ITransformedCatchUp;
    isCatchupLoading?: boolean;
    isMixCatchup?: boolean;
    handlePlayClick?: (catchupData) => void;
}

export default function CatchUpDetail({
    catchupData,
    isCatchupLoading,
    isMixCatchup,
    handlePlayClick,
}: ICatchupDetailProps) {
    const [episodeList, setEpisodeList] = useState([]);
    const [isEpisodeListLoading, setEpisodeListLoading] = useState(false);
    const [isEpisodeDetailLoading, setIsEpisodeDetailLoading] = useState(false);
    const navigate = useNavigate();
    const userChannels = useSelector((state: any) => state.liveTv?.userChannels);
    const channel = channelsUtil?.getChannelByStationId(catchupData?.stationId, userChannels);
    // Using React Query instead of Redux for configuration
    const { paidVsFree, channelImage } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable;
    const channelImageId = channelImage?.id || 'colour_264x264';
    const guids = channel?.subscriptions.map((el) => el?.subscriptionGUID) || [];
    const isLocked = guids.length > 0 && checkShowLockIcon(guids) && enableLockIcon;

    useEffect(() => {
        if (catchupData?.seriesId && catchupData?.stationCallSign) {
            getEpisodes();
        }
    }, [catchupData?.seriesId]);

    const getEpisodes = async () => {
        try {
            setEpisodeListLoading(true);
            if (isMixCatchup) {
                const response = await getMixedCatchUpEpisodesApi(catchupData?.seriesId);
                setEpisodeList(response);
            } else {
                const response = await getCatchUpEpisodesApi(catchupData?.seriesId, catchupData?.stationCallSign);
                setEpisodeList(response);
            }
        } finally {
            setEpisodeListLoading(false);
        }
    };

    const handleEpisodeClick = async (episode) => {
        try {
            setIsEpisodeDetailLoading(true);
            const timeRange = f2.getTimeRange(channel?.rights?.catchupBuffer);
            const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
            const response = await mpxService.getCatchUpEpisodeDetail({
                programId: episode.Id,
                startTime: timeRange ?? '',
            });
            if (response?.entries?.length > 0) {
                const episodeDetail: EpisodeDetailEntry = response.entries[0];
                const episodeChannel = channelsUtil.getChannelByStationId(episodeDetail.stationId, userChannels);
                handlePlayClick({
                    stationId: episodeDetail?.stationId ?? '',
                    title: episodeDetail?.program?.title ?? '',
                    startTime: moment(episodeDetail.startTime).valueOf(),
                    endTime: moment(episodeDetail.endTime).valueOf(),
                    tuningURLs: episodeChannel?.tuningURLs ?? [],
                    stationCallSign: catchupData.stationCallSign,
                    coverImage: episode?.image_landscape ?? '',
                    guid: episodeDetail?.guid,
                    paId: episodeDetail?.programId,
                    listingId: episodeDetail.id,
                    ratings: episode?.ratings,
                });
            } else {
                toast(__('catchup_episode_detail_not_found'), 'error');
            }
        } finally {
            setIsEpisodeDetailLoading(false);
        }
    };

    const getLoader = () => {
        return Array.from({ length: 4 }, (_, i) => {
            return <DetailedPoster isLoading={true} key={`catchup_episode_loader_${i}`} />;
        });
    };

    return (
        <>
            {isCatchupLoading || !catchupData ? (
                <>
                    <FlexWrapper flexDirection="column" gap="2x">
                        <HeroBlock isLoading={true}></HeroBlock>
                    </FlexWrapper>
                </>
            ) : (
                <FlexWrapper flexDirection="column" gap="2x">
                    <HeroBlock
                        heading={catchupData?.seriesTitle || catchupData.title}
                        picture={catchupData.coverImage}
                        compact
                        detail
                        // isLoading - for loading state
                    ></HeroBlock>
                    <FlexWrapper
                        flexDirection="column"
                        gap="2x"
                        paddingTop={{ default: '1x', md: '2x' }}
                        paddingBottom={{ default: '1x', md: '2x' }}
                    >
                        <MButtons
                            paddingBottom={{ md: '3x' }}
                            play={
                                <Button
                                    primary
                                    onClick={() => {
                                        if (handlePlayClick) {
                                            handlePlayClick(catchupData);
                                        }
                                    }}
                                >
                                    <span>{__('play_now')}</span>{' '}
                                    {isLocked ? (
                                        <FontIcon name="lock" />
                                    ) : (
                                        <FontIcon name="play" size="smaller" md="smaller" />
                                    )}
                                </Button>
                            }
                            className="details"
                        />
                        <MBDetails
                            rating={catchupData.ratings ? __(`rating_${catchupData.ratings.toLowerCase()}`) : null}
                            info={[
                                ...catchupData.genre, // Ensure item.genre is an array
                                catchupData.startTime && catchupData.endTime
                                    ? `${f.formatRewindDate(catchupData.startTime, true)} - ${f.formatRewindDate(
                                          catchupData.endTime,
                                          false
                                      )}`
                                    : undefined,
                            ].filter(Boolean)} // Filter out any undefined values
                            provider={[
                                channelsUtil.getSeletedChannelImage(channel, channelImageId)?.endsWith('.svg')
                                    ? channelsUtil.getSeletedChannelImage(channel, channelImageId)
                                    : h.getImagizerUrl(
                                          channelsUtil.getSeletedChannelImage(channel, channelImageId),
                                          80,
                                          60
                                      ),
                            ]}
                        />
                    </FlexWrapper>
                    {/* Episodes */}
                    <Text as="p" alignment="start" margin="4x" style={{ opacity: 0.9 }}>
                        {catchupData.description}
                    </Text>
                    {(catchupData?.seriesId && (
                        <>
                            <SectionHeaderWithPagination title={__('catchup_episodesTitle')} className="paging" />
                            <Carousel2
                                swiperSettings={{
                                    slidesPerGroup: 1,
                                    slidesPerView: 2,
                                    breakpoints: {
                                        560: {
                                            slidesPerView: 3,
                                        },
                                    },
                                    pagination: {
                                        el: '.paging',
                                        clickable: true,
                                    },
                                }}
                            >
                                {isEpisodeListLoading
                                    ? getLoader()
                                    : episodeList?.length > 0 &&
                                      episodeList
                                          ?.sort((a, b) => {
                                              if (a?.tvSeasonNumber !== b?.tvSeasonNumber) {
                                                  return a?.tvSeasonNumber - b.tvSeasonNumber;
                                              }
                                              return a?.tvSeasonEpisodeNumber - b?.tvSeasonEpisodeNumber;
                                          })
                                          ?.map((episode, indx) => {
                                              return (
                                                  <CatchUpCard
                                                      isEpisode={true}
                                                      catchupData={{
                                                          Id: episode.guid,
                                                          title: episode.text,
                                                          image: episode.image_landscape,
                                                          stationId: episode?.stationId ?? catchupData?.stationId,
                                                          ratings: episode?.ratings,
                                                          genre:
                                                              episode?.genre?.length > 0
                                                                  ? episode.genre
                                                                  : catchupData.genre,
                                                          tvSeasonNumber: episode?.tvSeasonNumber,
                                                          tvSeasonEpisodeNumber: episode?.tvSeasonEpisodeNumber,
                                                          dateTime: __('catchup_episode_carousel_hint_text'), // will be replaced later with exact date and time once available in the api.
                                                      }}
                                                      keyId={`catup-popup-episode-${episode.Id}-${indx}`}
                                                      handlePlayClick={() => {
                                                          handleEpisodeClick(episode);
                                                      }}
                                                      handleDetailCardClick={() => {
                                                          handleEpisodeClick(episode);
                                                      }}
                                                      isPlayButtonLoading={isEpisodeDetailLoading}
                                                  />
                                              );
                                          })}
                            </Carousel2>
                        </>
                    )) ||
                        ''}
                </FlexWrapper>
            )}
        </>
    );
}
